"use client";

import {
  type Column,
  flexRender,
  type RowData,
  type Table,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { TableColumnResizeHandle } from "@/components/ui/table-column-resize-handle";
import { InfiniteFeedLoader } from "@/components/app/inbox/infinite-feed-loader";
import { EmptyState } from "@/components/ui/empty-state";
import { cn } from "@/lib/utils";
import {
  TABLE_PINNED_COLUMN,
  TABLE_ROW_BACKGROUND,
  TABLE_ROW_HOVER_BACKGROUND,
  TABLE_ROW_STRIPE_BACKGROUND,
  TABLE_STICKY_HEADER,
  TableScrollContainer,
} from "@/components/ui/table-grid";
import {
  ACTIONS_COLUMN_ID,
  columnWidthStyle,
  fluidColumnWidthStyle,
} from "./record-table-state";
import {
  nextRecordTableCell,
  type RecordTableArrowKey,
  type RecordTableCell,
} from "./record-table-navigation";
import { createRecordTableFocusScheduler } from "./record-table-focus";
import type { GridTableFeatures } from "../../table-features";

const ROW_HEIGHT = 32;
const OVERSCAN_ROWS = 12;

export function RecordTableGrid<TData extends RowData>({
  table,
  scrollRef,
  caption,
  activeCell,
  editingCell,
  canEditCell,
  editOnClick,
  onSelectCell,
  onEditCell,
  empty,
  renderFooter,
  hasMore = false,
  isFetchingMore = false,
  onLoadMore,
  emptyMessage = "No records yet.",
  sort = null,
  canSortColumn,
  onSortColumn,
}: {
  table: Table<GridTableFeatures, TData>;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  caption?: string;
  activeCell: RecordTableCell | null;
  editingCell: RecordTableCell | null;
  canEditCell: (cell: RecordTableCell) => boolean;
  editOnClick: (cell: RecordTableCell) => boolean;
  onSelectCell: (cell: RecordTableCell, record: TData) => void;
  onEditCell: (cell: RecordTableCell) => void;
  empty: boolean;
  /** The sorted column, mirrored in its header. */
  sort?: { columnId: string; direction: "asc" | "desc" } | null;
  canSortColumn?: (columnId: string) => boolean;
  /** A header click; the owner cycles ascending, descending, off. */
  onSortColumn?: (columnId: string) => void;
  renderFooter?: (props: {
    columns: Column<GridTableFeatures, TData, unknown>[];
    tableWidth: number;
  }) => React.ReactNode;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  emptyMessage?: string;
}) {
  const rowModel = table.getRowModel();
  const virtualizer = useVirtualizer({
    count: rowModel.rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN_ROWS,
  });
  const virtualRows = virtualizer.getVirtualItems();
  const tableWidth = table.getTotalSize();
  const pinnedColumnClass = TABLE_PINNED_COLUMN;
  const lastVirtualIndex = virtualRows.at(-1)?.index ?? -1;
  const cellRefs = useRef(new Map<string, HTMLTableCellElement>());
  const pendingFocus = useMemo(
    () =>
      createRecordTableFocusScheduler(
        (callback) => window.requestAnimationFrame(callback),
        (id) => window.cancelAnimationFrame(id),
      ),
    [],
  );
  useEffect(() => () => pendingFocus.cancel(), [pendingFocus]);
  const dataColumns = table
    .getAllLeafColumns()
    .filter((column) => column.id !== ACTIONS_COLUMN_ID);
  const focusCell = useCallback(
    (cell: RecordTableCell, edit = false) => {
      pendingFocus.cancel();
      const rowIndex = rowModel.rows.findIndex((row) => row.id === cell.rowId);
      if (rowIndex < 0) return;
      virtualizer.scrollToIndex(rowIndex, { align: "auto" });
      const focus = () => {
        const element = cellRefs.current.get(`${cell.rowId}:${cell.columnId}`);
        if (edit) {
          // An auto-opened dropdown owns focus inside its portal.
          if (element?.querySelector('[aria-haspopup][aria-expanded="true"]'))
            return;
          element
            ?.querySelector<HTMLElement>(
              "input, textarea, select, button, [role='combobox']",
            )
            ?.focus();
        } else {
          element?.focus();
        }
      };
      if (!edit && cellRefs.current.has(`${cell.rowId}:${cell.columnId}`))
        focus();
      else pendingFocus.schedule(focus);
    },
    [pendingFocus, rowModel.rows, virtualizer],
  );

  useEffect(() => {
    if (!hasMore || isFetchingMore || !onLoadMore || rowModel.rows.length === 0)
      return;
    if (lastVirtualIndex >= rowModel.rows.length - 12) onLoadMore();
  }, [
    hasMore,
    isFetchingMore,
    lastVirtualIndex,
    onLoadMore,
    rowModel.rows.length,
  ]);

  useEffect(() => {
    if (!activeCell) return;
    const rowIndex = rowModel.rows.findIndex(
      (row) => row.id === activeCell.rowId,
    );
    if (rowIndex >= 0) virtualizer.scrollToIndex(rowIndex, { align: "auto" });
  }, [activeCell, rowModel.rows, virtualizer]);

  return (
    <TableScrollContainer
      ref={scrollRef}
      className="mlx-floating-panel-scroll-surface"
    >
      <table
        role="grid"
        className="grid w-full border-separate border-spacing-0 text-sm"
        style={{ minWidth: tableWidth }}
      >
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead className={cn(TABLE_STICKY_HEADER, "z-10")}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="flex w-full text-left">
              {headerGroup.headers.map((header) => {
                const label = header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    );
                const sortable =
                  Boolean(onSortColumn) &&
                  (canSortColumn?.(header.column.id) ?? false);
                const sorted =
                  sort?.columnId === header.column.id ? sort.direction : null;
                return (
                  <th
                    key={header.id}
                    aria-sort={
                      sorted
                        ? sorted === "asc"
                          ? "ascending"
                          : "descending"
                        : undefined
                    }
                    className={cn(
                      "relative flex h-7 items-center border-b border-r border-border/40 px-2 py-0 whitespace-nowrap text-xs font-medium tracking-normal text-muted-foreground last:border-r-0",
                      header.column.id === ACTIONS_COLUMN_ID
                        ? `${pinnedColumnClass} z-30 justify-center bg-background px-0`
                        : "",
                    )}
                    style={
                      header.column.id === ACTIONS_COLUMN_ID
                        ? columnWidthStyle(header.getSize())
                        : fluidColumnWidthStyle(header.getSize())
                    }
                  >
                    {sortable ? (
                      <button
                        type="button"
                        className={cn(
                          "flex min-w-0 flex-1 items-center gap-1 rounded-sm text-left hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                          sorted ? "text-foreground" : null,
                        )}
                        aria-label={`Sort by ${String(header.column.columnDef.header ?? "column")}`}
                        onClick={() => onSortColumn?.(header.column.id)}
                      >
                        <span className="truncate">{label}</span>
                        {sorted === "asc" ? (
                          <ArrowUpIcon
                            className="size-3 shrink-0"
                            aria-hidden
                          />
                        ) : sorted === "desc" ? (
                          <ArrowDownIcon
                            className="size-3 shrink-0"
                            aria-hidden
                          />
                        ) : null}
                      </button>
                    ) : (
                      <span className="truncate">{label}</span>
                    )}
                    {header.column.getCanResize() ? (
                      <TableColumnResizeHandle
                        aria-label={`Resize ${String(header.column.columnDef.header ?? "column")}`}
                        active={header.column.getIsResizing()}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                      />
                    ) : null}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody
          className="relative grid"
          style={{ height: `${virtualizer.getTotalSize()}px` }}
        >
          {virtualRows.map((virtualRow) => {
            const row = rowModel.rows[virtualRow.index]!;
            return (
              <tr
                key={row.id}
                data-index={virtualRow.index}
                data-selected={activeCell?.rowId === row.id}
                ref={(node) => virtualizer.measureElement(node)}
                className={cn(
                  "group/row absolute flex h-8",
                  virtualRow.index % 2 === 0
                    ? TABLE_ROW_STRIPE_BACKGROUND
                    : TABLE_ROW_BACKGROUND,
                  TABLE_ROW_HOVER_BACKGROUND,
                  "[&[data-selected=true]>td]:[background-color:color-mix(in_oklab,hsl(var(--muted))_72%,hsl(var(--surface)))]",
                )}
                style={{
                  transform: `translateY(${virtualRow.start}px)`,
                  width: "100%",
                  minWidth: tableWidth,
                }}
              >
                {row.getAllCells().map((cell) => {
                  const tableCell = { rowId: row.id, columnId: cell.column.id };
                  const selectable = cell.column.id !== ACTIONS_COLUMN_ID;
                  const active =
                    selectable &&
                    activeCell?.rowId === tableCell.rowId &&
                    activeCell.columnId === tableCell.columnId;
                  const editing =
                    active &&
                    editingCell?.rowId === tableCell.rowId &&
                    editingCell.columnId === tableCell.columnId;
                  return (
                    <td
                      key={cell.id}
                      ref={(element) => {
                        const key = `${tableCell.rowId}:${tableCell.columnId}`;
                        if (element) cellRefs.current.set(key, element);
                        else cellRefs.current.delete(key);
                      }}
                      role={selectable ? "gridcell" : undefined}
                      tabIndex={selectable ? (active ? 0 : -1) : undefined}
                      aria-selected={active || undefined}
                      data-active-cell={active || undefined}
                      className={cn(
                        "flex max-w-0 items-center border-r border-b border-border/40 px-2 py-0.5 align-middle last:border-r-0",
                        // The pinned cell inherits its row's opaque [&>td] fill, so
                        // it matches the row exactly and needs no per-cell override.
                        cell.column.id === ACTIONS_COLUMN_ID
                          ? cn(pinnedColumnClass, "justify-center px-0")
                          : "",
                        active &&
                          "relative z-10 outline-2 -outline-offset-2 outline-ring",
                      )}
                      style={
                        cell.column.id === ACTIONS_COLUMN_ID
                          ? columnWidthStyle(cell.column.getSize())
                          : fluidColumnWidthStyle(cell.column.getSize())
                      }
                      onClick={
                        selectable
                          ? (event) => {
                              if (
                                editing ||
                                !event.currentTarget.contains(
                                  event.target as Node,
                                )
                              )
                                return;
                              pendingFocus.cancel();
                              onSelectCell(tableCell, row.original);
                              event.currentTarget.focus();
                              if (
                                canEditCell(tableCell) &&
                                editOnClick(tableCell)
                              ) {
                                onEditCell(tableCell);
                                focusCell(tableCell, true);
                              }
                            }
                          : undefined
                      }
                      onDoubleClick={
                        selectable
                          ? () => {
                              if (editing) return;
                              onSelectCell(tableCell, row.original);
                              if (!canEditCell(tableCell)) return;
                              onEditCell(tableCell);
                              focusCell(tableCell, true);
                            }
                          : undefined
                      }
                      onKeyDown={
                        selectable
                          ? (event) => {
                              if (editing) return;
                              if (event.key === "Enter") {
                                if (!canEditCell(tableCell)) return;
                                event.preventDefault();
                                onEditCell(tableCell);
                                focusCell(tableCell, true);
                                return;
                              }
                              if (
                                ![
                                  "ArrowUp",
                                  "ArrowDown",
                                  "ArrowLeft",
                                  "ArrowRight",
                                ].includes(event.key)
                              )
                                return;
                              const nextCell = nextRecordTableCell(
                                rowModel.rows.map((candidate) => candidate.id),
                                dataColumns.map((column) => column.id),
                                tableCell,
                                event.key as RecordTableArrowKey,
                              );
                              if (!nextCell) return;
                              const nextRow = rowModel.rows.find(
                                (candidate) => candidate.id === nextCell.rowId,
                              );
                              if (!nextRow) return;
                              event.preventDefault();
                              onSelectCell(nextCell, nextRow.original);
                              focusCell(nextCell);
                            }
                          : undefined
                      }
                    >
                      <div
                        className={cn(
                          "w-full min-w-0",
                          !editing && cell.column.id !== ACTIONS_COLUMN_ID &&
                            "overflow-hidden whitespace-nowrap",
                          cell.column.id === ACTIONS_COLUMN_ID
                            ? "flex justify-center"
                            : "flex-1",
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        {renderFooter?.({
          columns: table.getAllLeafColumns(),
          tableWidth,
        })}
      </table>
      {empty ? <EmptyState>{emptyMessage}</EmptyState> : null}
      {!empty && onLoadMore ? (
        <InfiniteFeedLoader
          hasMore={hasMore}
          isLoading={isFetchingMore}
          noun="records"
          onLoadMore={onLoadMore}
        />
      ) : null}
    </TableScrollContainer>
  );
}
